/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import { gnoment } from 'shared-ui/gnoment';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { DividerNew as Divider, FlexRow, FlexCol, Stack } from 'sarsaparilla';
import FeeEstimate from './FeeEstimate';
import OrderDetailsItinerary from './OrderDetailsItinerary';

import * as types from '../../constants';
import {
    isItineraryPermit,
    getDayCardinality,
    hasLotteryPreferenceDateDivisionDuration,
    hasLotteryPreferenceDateDivisionGroupSize,
    hasLotteryPreferenceDateDivisionMaxGroupSizeOnly,
    shouldIncrementCheckoutDate,
    isParkingPermit,
} from '../../utils/utilsNew';
import EntryExitDateText from './EntryExitDateText';

const propTypes = {
    name: PropTypes.string,
    permit: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        divisions: PropTypes.shape({}),
    }).isRequired,
    issuance: PropTypes.shape({
        type: PropTypes.int,
        start_date: PropTypes.string,
    }),
    itinerary: PropTypes.arrayOf(PropTypes.shape({})),
    entryDate: PropTypes.shape({}),
    exitDate: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
    isExtended: PropTypes.bool,
    groupMembers: PropTypes.arrayOf(PropTypes.object),
    watercrafts: PropTypes.arrayOf(PropTypes.object),
    animals: PropTypes.arrayOf(PropTypes.object),
    isLottery: PropTypes.bool,
    issuanceStatus: PropTypes.number.isRequired,
    amountPaid: PropTypes.number.isRequired,
    division: PropTypes.shape({
        id: PropTypes.string,
        fees: PropTypes.arrayOf(
            PropTypes.shape({
                amount: PropTypes.number,
                description: PropTypes.string,
                is_lottery: PropTypes.bool,
                range_end: PropTypes.number,
                range_start: PropTypes.number,
                sales_channel_type: PropTypes.number,
                sku: PropTypes.string,
                type: PropTypes.number,
            })
        ),
        fee_calculation_model: PropTypes.number,
        fee_display_model: PropTypes.number,
        name: PropTypes.string,
    }).isRequired,
    selectedDates: PropTypes.arrayOf(PropTypes.shape({})),
    disableButtons: PropTypes.bool,
    buttonLabels: PropTypes.shape({
        proceed: PropTypes.string,
        continue: PropTypes.string,
        cancel: PropTypes.string,
    }),
    lineItems: PropTypes.array,
    isQuotaModifyAllowed: PropTypes.bool,
    hasTripDatesLocked: PropTypes.bool,
    isHighUseFeeApplicable: PropTypes.bool,
    isCommercialUser: PropTypes.bool,
    reservationDetailName: PropTypes.string,
    guides: PropTypes.array,
    permitMapping: PropTypes.shape({}),
};

const defaultProps = {
    groupMembers: [],
    watercrafts: [],
    animals: [],
    buttonLabels: {
        proceed: 'Proceed to Cart',
        continue: 'Continue Shopping',
        cancel: 'Remove from cart',
    },
};

class ReservationDetail extends React.Component {
    static propTypes = propTypes;
    static defaultProps = defaultProps;

    renderLengthOfStayPreference(preference) {
        const lengthOfStay = preference.length_of_stay
            ? `${preference.length_of_stay} ${getDayCardinality(preference.length_of_stay)}`
            : '';

        return <div className="stay-length">{lengthOfStay}</div>;
    }

    renderGroupSizePreference(preference, maxOnly) {
        const groupMinSize = preference.group_min_size;
        const groupMaxSize = preference.group_max_size;
        let groupSelection = '';
        // Display group size only if both values have been selected and are valid
        if (groupMinSize && groupMaxSize && !maxOnly) {
            groupSelection += `${groupMinSize} - ${groupMaxSize} people`;
        } else if (groupMaxSize && maxOnly) {
            groupSelection = `${groupMaxSize} people`;
        }

        return <div className="group-size">{groupSelection}</div>;
    }

    renderMultipleDivisionPreferenceDetail(date, idx) {
        const divisionId = date.division_id;
        const divisionName = this.props.permit.divisions[divisionId]
            ? this.props.permit.divisions[divisionId].name
            : '';
        const selectedDate = date.preferred_date;

        if (gnoment.isGnoment(selectedDate) && selectedDate.isValid()) {
            const key = `${selectedDate.format()}-${idx}`;
            return (
                <div key={key} className="selected-date">
                    <FlexRow>
                        <FlexCol xs={1}>
                            <strong>#{idx + 1}.</strong>
                        </FlexCol>
                        <FlexCol xs={11}>
                            <div className="date">
                                {selectedDate.format(types.DATE_FORMAT)}
                            </div>
                            <div className="division">{divisionName}</div>
                            {hasLotteryPreferenceDateDivisionDuration(
                                this.props.permit,
                                undefined
                            )
                                ? this.renderLengthOfStayPreference(date)
                                : null}
                            {hasLotteryPreferenceDateDivisionGroupSize(
                                this.props.permit,
                                undefined
                            )
                                ? this.renderGroupSizePreference(
                                      date,
                                      hasLotteryPreferenceDateDivisionMaxGroupSizeOnly(
                                          this.props.permit,
                                          undefined
                                      )
                                  )
                                : null}
                        </FlexCol>
                    </FlexRow>
                </div>
            );
        }

        return null;
    }

    renderMultipleDivisionsPreferenceRangeDetails(date, idx) {
        const {
            division_id: divisionId,
            preferred_range_start: rangeStart,
            preferred_range_end: rangeEnd,
            group_max_size: groupMaxSize,
        } = date;
        const isValidMoment = (momentDate) =>
            gnoment.isGnoment(momentDate) && momentDate.isValid();

        if (!isValidMoment(rangeStart) || !isValidMoment(rangeEnd)) return null;

        const divisionName = this.props.permit.divisions[divisionId]
            ? this.props.permit.divisions[divisionId].name
            : '';
        const formattedDate = rangeStart.isSame(rangeEnd)
            ? rangeStart.format(types.DATE_FORMAT)
            : `${rangeStart.format(types.DATE_FORMAT)} - ${rangeEnd.format(types.DATE_FORMAT)}`;
        const key = `${formattedDate}-${idx}`;

        return (
            <div key={key} className="selected-date">
                <FlexRow>
                    <FlexCol xs={1}>
                        <strong>#{idx + 1}.</strong>
                    </FlexCol>
                    <FlexCol xs={11}>
                        <div className="date">{formattedDate}</div>
                        <div className="division">{divisionName}</div>
                        <div className="group-size">{`${groupMaxSize} ${groupMaxSize === 1 ? 'person' : 'people'}`}</div>
                    </FlexCol>
                </FlexRow>
            </div>
        );
    }

    renderLotteryReservationDatesText() {
        const { selectedDates } = this.props;
        return (
            <div className="per-os-lottery-preferences">
                <div className="per-os-lottery-preferences-title">
                    Lottery Preferences ({selectedDates.length})
                </div>

                {selectedDates.map((date, i) => {
                    if (date) {
                        if (
                            !isEmpty(date.preferred_date) &&
                            gnoment(date.preferred_date).year() > 2000
                        ) {
                            return this.renderMultipleDivisionPreferenceDetail(date, i);
                        }
                        if (
                            !isEmpty(date.preferred_range_start) &&
                            !isEmpty(date.preferred_range_end)
                        ) {
                            return this.renderMultipleDivisionsPreferenceRangeDetails(
                                date,
                                i
                            );
                        }
                    }
                    // otherwise return preference placeholder until further preference data is received
                    return (
                        <div key={i.toString()} className="selected-date">
                            ---
                        </div>
                    );
                })}
            </div>
        );
    }

    renderPermitDateText() {
        const { itinerary, entryDate, exitDate, permit, division, permitMapping } =
            this.props;
        const divisions = [division.id];
        const isItinerary = isItineraryPermit(permit, division.id, permitMapping);

        if (isItinerary) {
            return (
                <OrderDetailsItinerary
                    incrementCheckoutDate={shouldIncrementCheckoutDate(
                        get(permit, 'rules', []),
                        divisions,
                        get(permit, 'id', undefined)
                    )}
                    itinerary={itinerary}
                    divisions={permit.divisions}
                />
            );
        }
        return (
            <EntryExitDateText
                incrementCheckoutDate={shouldIncrementCheckoutDate(
                    get(permit, 'rules', []),
                    divisions,
                    get(permit, 'id', undefined)
                )}
                permit={permit}
                entryDate={entryDate}
                exitDate={exitDate}
            />
        );
    }

    renderPermitParkingText() {
        const { permit, division } = this.props;
        // Parking text is for Fossil Creek only right now...
        // It's parking lot name is its division name
        if (isParkingPermit(permit.id)) {
            return (
                <>
                    <div className="rec-order-summary-item-dates">
                        <FlexRow>
                            <FlexCol>
                                <div className="rec-dates-label">Parking Lot</div>
                                <div className="rec-dates-value">{division.name}</div>
                            </FlexCol>
                        </FlexRow>
                    </div>
                    <Divider />
                </>
            );
        }
        return null;
    }

    render() {
        return (
            <Stack className="per-reservation-detail">
                {this.props.isLottery
                    ? this.renderLotteryReservationDatesText()
                    : this.renderPermitDateText()}
                <Divider />
                {this.renderPermitParkingText()}
                <FeeEstimate
                    lineItems={this.props.lineItems}
                    issuance={this.props.issuance}
                    division={this.props.division}
                    permit={this.props.permit}
                    isLottery={this.props.isLottery}
                    entryDate={this.props.entryDate}
                    exitDate={this.props.exitDate}
                    isExtended={this.props.isExtended}
                    groupMembers={this.props.groupMembers}
                    guides={this.props.guides}
                    watercrafts={this.props.watercrafts}
                    animals={this.props.animals}
                    fees={this.props.division.fees || []}
                    feeCalculationModel={this.props.division.fee_calculation_model}
                    feeDisplayModel={this.props.division.fee_display_model}
                    issuanceStatus={this.props.issuanceStatus}
                    amountPaid={this.props.amountPaid}
                    isHighUseFeeApplicable={this.props.isHighUseFeeApplicable}
                />
            </Stack>
        );
    }
}

export default ReservationDetail;
